import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Row, Select, Space, Spin, Switch, Table, Typography, message } from "antd";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";

const MODES = [
  { value: "prorateCurrentMonth", label: "Prorate current month" },
  { value: "prorateNextMonth", label: "Prorate next month" },
  { value: "noProrate", label: "No prorate" },
  { value: "managerMovein", label: "Manager move-in · no monthly rent" },
];

const dateText = (date) => date?.format("YYYY-MM-DD");
const businessToday = () => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date()).map(({ type, value }) => [type, value]));
  return dayjs(`${parts.year}-${parts.month}-${parts.day}`);
};

export default function MoveInModal({ open, application, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [date, setDate] = useState(businessToday);
  const [mode, setMode] = useState("prorateNextMonth");
  const [lines, setLines] = useState([]);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState(null);
  const [nextInvoice, setNextInvoice] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [edited, setEdited] = useState(false);
  const lineKey = useRef(0);
  const initializedFor = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveInFlight = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    if (!application?.applicationId || initializedFor.current === application.applicationId) return;
    initializedFor.current = application.applicationId;
    setDate(businessToday());
    setMode("prorateNextMonth");
    form.resetFields();
    form.setFieldsValue({ applicantMembers: (application.applicants || []).map((person) => ({
      applicationInfoId: person.applicationInfoId,
      phone: person.phone,
      email: person.email,
      manager: false,
    })) });
  }, [open, application?.applicationId, application?.applicants, form]);

  useEffect(() => {
    if (!open || !application?.applicationId || !date) return;
    let active = true;
    setLines([]);
    setNextInvoice(null);
    setPreviewError(false);
    setEdited(false);
    const load = async () => {
      setLoading(true);
      try {
        const result = await http.get(api.creditCheckMoveInPreview, {
          params: { applicationId: application.applicationId, moveInDate: dateText(date), billingMode: mode },
        });
        if (result?.code !== "1") throw new Error(result?.msg || "Could not prepare billing");
        if (active) {
          setLines((result.data?.lines || []).map((line) => ({
            ...line,
            key: `preview-${++lineKey.current}`,
            startDate: dayjs(line.startDate),
            stopDate: dayjs(line.stopDate),
          })));
          setNextInvoice(result.data?.nextMonthInvoiceDate);
          setServices(result.data?.services || []);
        }
      } catch (error) {
        if (active) setPreviewError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [open, application?.applicationId, date, mode, previewAttempt]);

  const editLine = (index, patch) => {
    setEdited(true);
    setLines((previous) => previous.map((line, position) =>
      position === index ? { ...line, ...patch } : line
    ));
  };

  const changePlan = (apply) => {
    const replace = () => {
      setLoading(true);
      setLines([]);
      setNextInvoice(null);
      apply();
    };
    if (!edited) return replace();
    Modal.confirm({
      title: "Replace edited billing lines?",
      content: "Changing the date or plan will load a new billing schedule and discard your edits.",
      okText: "Load new schedule",
      onOk: replace,
    });
  };

  const save = async () => {
    if (saveInFlight.current || loading || previewError || !lines.length) return;
    saveInFlight.current = true;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        applicationId: application.applicationId,
        moveInDate: dateText(date),
        billingMode: mode,
        billingLines: lines.map((line) => ({
          serviceCode: String(line.serviceCode),
          remark: line.remark,
          amount: Number(line.amount || 0),
          startDate: dateText(line.startDate),
          stopDate: dateText(line.stopDate),
        })),
        water: values.water,
        gas: values.gas,
        electricity: values.electricity,
        trash: values.trash,
        sewer: values.sewer,
        landscape: Boolean(values.landscape),
        alternativeAddress: values.alternativeAddress,
        applicantMembers: values.applicantMembers || [],
        extraMembers: values.extraMembers || [],
      };
      const result = await http.post(api.creditCheckMoveIn, payload);
      if (result?.code !== "1") throw new Error(result?.msg || "Move-in could not be completed");
      message.success(`Move-in completed · Tenant #${result.data?.tenantId}`);
      onSaved?.(result.data);
    } catch (error) {
      if (!error?.errorFields) message.error(error.message || "Move-in could not be completed");
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const columns = [
    { title: "Service", dataIndex: "serviceCode", width: 100 },
    { title: "Remark", dataIndex: "remark", render: (value, line, index) =>
      <Input value={value} onChange={(event) => editLine(index, { remark: event.target.value })} /> },
    { title: "Amount", dataIndex: "amount", width: 130, render: (value, line, index) =>
      <InputNumber min={0} precision={2} value={Number(value)} onChange={(amount) => editLine(index, { amount })} style={{ width: "100%" }} /> },
    { title: "Starts", dataIndex: "startDate", width: 150, render: (value, line, index) =>
      <DatePicker value={value} onChange={(startDate) => editLine(index, { startDate })} format="MM/DD/YYYY" /> },
    { title: "Ends", dataIndex: "stopDate", width: 150, render: (value, line, index) =>
      <DatePicker value={value} onChange={(stopDate) => editLine(index, { stopDate })} format="MM/DD/YYYY" /> },
    { title: "", width: 75, render: (_, line, index) =>
      <Button type="link" danger onClick={() => {
        setEdited(true);
        setLines((previous) => previous.filter((_, position) => position !== index));
      }}>Remove</Button> },
  ];

  return (
    <Modal
      open={open}
      className="cc-movein-modal"
      width={1040}
      title={<div><strong>Complete move-in</strong><div className="cc-modal-subtitle">Review the billing schedule before creating the tenant and invoices.</div></div>}
      onCancel={onClose}
      closable={!saving} keyboard={!saving} maskClosable={false}
      footer={<Space><Button disabled={saving} onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} disabled={loading || previewError || !lines.length} onClick={save}>Create tenant and billing</Button></Space>}
      destroyOnClose
    >
      <Spin spinning={saving}>
      <div className="cc-movein-controls">
        <div><Typography.Text type="secondary">Move-in date</Typography.Text><DatePicker value={date} onChange={(value) => changePlan(() => setDate(value))} format="MM/DD/YYYY" allowClear={false} /></div>
        <div><Typography.Text type="secondary">Billing plan</Typography.Text><Select value={mode} onChange={(value) => changePlan(() => setMode(value))} options={MODES} style={{ width: 270 }} /></div>
      </div>
      {previewError && <Alert type="error" showIcon className="cc-inline-alert" message="Billing preview could not be loaded"
        description="Review the date and try again before completing move-in."
        action={<Button size="small" onClick={() => setPreviewAttempt((attempt) => attempt + 1)}>Retry</Button>} />}
      <div className="cc-billing-heading"><strong>Billing schedule</strong><Typography.Text type="secondary">{lines.length} line{lines.length === 1 ? "" : "s"} · review amounts and dates before saving</Typography.Text></div>
      {nextInvoice && lines.some((line) => ["602", "603"].includes(String(line.serviceCode))
        && dateText(line.startDate) === nextInvoice && dateText(line.stopDate) > nextInvoice) &&
        <Alert type="info" showIcon className="cc-inline-alert" message={`The next-month billing cutoff has passed. An IN dated ${nextInvoice} will be created now for the applicable line.`} />}
      <Table
        rowClassName="table-row-gradient"
        rowKey="key"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={lines}
        pagination={false}
        scroll={{ x: 850 }}
        className="cc-billing-table"
      />
      <Space wrap>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Choose an extra service"
          style={{ width: 275 }}
          value={newService}
          onChange={setNewService}
          options={services.map((service) => ({ value: service.serviceCode, label: `${service.serviceCode} · ${service.remark}` }))}
        />
        <Button disabled={!newService} onClick={() => {
          const service = services.find((item) => item.serviceCode === newService);
          setEdited(true);
          setLines((previous) => [...previous, {
            key: `extra-${++lineKey.current}`,
            serviceCode: newService,
            remark: service?.remark || "OTHER CHARGE",
            amount: 0,
            startDate: date,
            stopDate: date,
          }]);
          setNewService(null);
        }}>Add billing line</Button>
      </Space>
      <Form form={form} layout="vertical">
        <Divider orientation="left">Applicants moving in</Divider>
        {(application?.applicants || []).map((person, index) => (
          <Row gutter={10} align="middle" key={person.applicationInfoId}>
            <Form.Item name={["applicantMembers", index, "applicationInfoId"]} hidden><Input /></Form.Item>
            <Col xs={24} md={5}><strong>{person.firstName} {person.lastName}</strong></Col>
            <Col xs={24} md={6}><Form.Item name={["applicantMembers", index, "phone"]} label="Phone"><Input /></Form.Item></Col>
            <Col xs={24} md={8}><Form.Item name={["applicantMembers", index, "email"]} label="Email"><Input /></Form.Item></Col>
            <Col xs={24} md={5}><Form.Item name={["applicantMembers", index, "manager"]} label="Manager" valuePropName="checked"><Switch /></Form.Item></Col>
          </Row>
        ))}
        <Divider orientation="left">Utilities and alternate address</Divider>
        <Row gutter={14}>
          {["water", "gas", "electricity", "trash", "sewer"].map((field) =>
            <Col xs={24} sm={12} md={8} key={field}><Form.Item name={field} label={field[0].toUpperCase() + field.slice(1)}><Input /></Form.Item></Col>
          )}
          <Col xs={24} sm={12} md={8}><Form.Item name="landscape" label="Landscape"><Select options={[{ value: false, label: "No" }, { value: true, label: "Yes" }]} /></Form.Item></Col>
        </Row>
        <Row gutter={14}>
          <Col xs={24} md={10}><Form.Item name={["alternativeAddress", "street"]} label="Alternative street"><Input /></Form.Item></Col>
          <Col xs={24} md={6}><Form.Item name={["alternativeAddress", "city"]} label="City"><Input /></Form.Item></Col>
          <Col xs={12} md={4}><Form.Item name={["alternativeAddress", "state"]} label="State"><Input maxLength={2} /></Form.Item></Col>
          <Col xs={12} md={4}><Form.Item name={["alternativeAddress", "zip"]} label="ZIP"><Input /></Form.Item></Col>
        </Row>
        <Divider orientation="left">Additional residents</Divider>
        <Form.List name="extraMembers">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }, index) => (
                <Card key={key} size="small" className="cc-applicant-card cc-extra-member-card"
                  title={`Additional resident ${index + 1}`}
                  extra={<Button type="link" danger onClick={() => remove(name)}>Remove</Button>}>
                  <Row gutter={12}>
                    <Col xs={24} md={8}><Form.Item name={[name, "firstName"]} label="First name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "middleName"]} label="Middle name"><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "lastName"]} label="Last name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "suffix"]} label="Suffix"><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "phone"]} label="Phone"><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "email"]} label="Email"><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "socialSecurity"]} label="Social Security number"><Input.Password autoComplete="off" /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "driverLicense"]} label="Driver license"><Input /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "driverLicenseState"]} label="License state"><Input maxLength={2} /></Form.Item></Col>
                    <Col xs={24} md={8}><Form.Item name={[name, "manager"]} label="On-site manager" valuePropName="checked"><Switch /></Form.Item></Col>
                  </Row>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()}>Add resident</Button>
            </>
          )}
        </Form.List>
      </Form>
      </Spin>
    </Modal>
  );
}
